import Vue from "vue";
import Vuex from "vuex";
import persistence from "./persistence";
// FT-1163: stamps WHEN a seat died, at the instant the shroud goes down —
// the only instant that fact exists. See the file header.
import deathMoment from "./deathMoment";
import socket from "./socket";
// FT-1491: the edition frame strips storyteller-only sections for players.
import { playerSections } from "../golem/roleSections";
import players from "./modules/players";
import session from "./modules/session";
// FT-860: the storyteller's night checklist + append-only night log.
import night from "./modules/night";
// FT-965: THE TOWN LOG. The merge/cursor/visibility rules live in the module,
// not in these mutations — see golem/chat.js for why "no gap, no duplicate" is
// a property of the data structure rather than of arrival timing.
import {
  canSee,
  catchUp,
  levelSilences,
  mergeLog,
  SCOPES,
  seatOf,
  viewerOf,
} from "../golem/chat";
// FT-1263: a bystander's traffic row — the plane's memory in the Chronicle.
import { TRAFFIC_KIND } from "../golem/whisperMarks";
// FT-1349: the catch-up read is filtered server-side to the requester; the
// storyteller of an owned town proves the role with the town's edit key.
import { editKeyFor } from "../golem/towns";
// FT-1206: the town's chat level rides the tower shelf; ingest reads it as a
// plain snapshot (towerState is the module's own single copy, sanitized on
// every write), the same way the components snapshot it.
import { towerState } from "../golem/towerBells";
// FT-1019: the chronicles stream's filter vocabulary — the store holds which
// one is showing so the V hotkey can arm "gallows" before the drawer opens.
import { FILTERS } from "../golem/chronicles";
// FT-1040: a FORGED role's composed night action (role.golemNight) registers
// into the night schema as the script loads, so the checklist, the player
// prompt and the chronicle all answer for it natively — see nightInfo's
// AUTHORED NIGHT ACTIONS section.
import { registerAuthoredNight, resetAuthoredNight } from "../golem/nightInfo";
// FT-1475: a custom role's own night words register into the night signs
// registry as the script lands (setCustomRoles). FT-1486: a script's own
// `_meta.nightCopy` is no longer read — see nightSigns' COPY AS DATA.
import { registerScriptRoleCopy } from "../golem/nightSigns";
// FT-1463: the night ORDER is the script's two lists — built from the seeds
// when a script has none, washed on every landing (setEdition), read by
// every ordered night surface through the nightLists / nightWakes / nightKey
// getters below.
import {
  NIGHTS,
  isTraveller,
  orderKey,
  resolveList,
  sanitizeList,
  wakesIn,
} from "../golem/nightOrder";
// FT-1484 step 3: the custom-role keys a library row decides — what the live
// overlay (liveRolesApplied below) rewrites on a script's snapshot.
import { LIVE_ROLE_KEYS } from "../golem/roles";
import editionJSON from "../editions.json";
import rolesJSON from "../roles.json";
import fabledJSON from "../fabled.json";

Vue.use(Vuex);

// The first bundled edition is the default script (Trouble Brewing).
const defaultEdition = editionJSON[0];

// Every character list renders grouped by team, the team names in REVERSE
// alphabetical order: traveler, townsfolk, outsider, minion, demon. The sort
// is stable, so characters of one team keep the order they came in.
const byTeamReversed = (left, right) => -left.team.localeCompare(right.team);

// A bundled character belongs to an edition when it was printed in it, or
// when the edition lists it by id (the mixed bundled scripts do that).
const inEdition = (edition, role) =>
  role.edition === edition.id || edition.roles.includes(role.id);

/** The bundled characters of one edition, id → role, in team order. */
function getRolesByEdition(edition = defaultEdition) {
  const members = rolesJSON.filter((role) => inEdition(edition, role));
  return new Map(Array.from(members.sort(byTeamReversed), (r) => [r.id, r]));
}

/** Every bundled traveler the edition does not already carry, file order. */
function getTravelersNotInEdition(edition = defaultEdition) {
  const travelers = new Map();
  for (const role of rolesJSON) {
    if (role.team !== "traveler" || inEdition(edition, role)) continue;
    travelers.set(role.id, role);
  }
  return travelers;
}

const set =
  (key) =>
  ({ grimoire }, val) => {
    grimoire[key] = val;
  };

const toggle =
  (key) =>
  ({ grimoire }, val) => {
    if (val === true || val === false) {
      grimoire[key] = val;
    } else {
      grimoire[key] = !grimoire[key];
    }
  };
const clean = (id) => id.toLocaleLowerCase().replace(/[^a-z0-9]/g, "");
const editionJSONbyId = new Map(
  editionJSON.map((edition) => [edition.id, edition]),
);
const rolesJSONbyId = new Map(rolesJSON.map((role) => [role.id, role]));
const fabled = new Map(fabledJSON.map((role) => [role.id, role]));
// The custom-role template. Its KEY ORDER is a wire and storage contract: the
// compact form of a custom script (customRolesStripped below, the edition
// frame, the `roles` stash, copied-out game JSON) addresses each property by
// its position in this list. Never reorder, never insert — only append.
const customRole = {
  id: "",
  name: "",
  image: "",
  ability: "",
  edition: "custom",
  firstNight: 0,
  firstNightReminder: "",
  otherNight: 0,
  otherNightReminder: "",
  reminders: [],
  remindersGlobal: [],
  setup: false,
  team: "townsfolk",
  isCustom: true,
  // Golem fork (FT-851): appended LAST so upstream's numeric key mapping is
  // unchanged. golemIcon = an official role id whose bundled icon this custom
  // role reuses; golemRoleId = the role-library id (lineage/edit handle).
  golemIcon: "",
  golemRoleId: "",
  // FT-1040: the composed night action the forge authored — a full schema
  // entry ({wakes, fields, label, line, mayBeFalse}), null when none was
  // composed. In this template so it survives customRolesStripped's wire
  // form (only template keys ride the numeric mapping to other clients).
  golemNight: null,
  // FT-1042: the art's FIT on the coin — scale (0.4–2, default 1) and x/y
  // offsets in percent of the coin's width (±50, default 0). Appended LAST
  // so the numeric wire mapping's existing keys are unchanged; in this
  // template so the fit rides customRolesStripped to every client — the
  // adjustment is part of the role, 1:1 in game. Token.vue clamps at render.
  golemArtScale: 1,
  golemArtX: 0,
  golemArtY: 0,
  // FT-1475: the role's own night WORDING — the Role Studio's Words, the
  // seven copy keys ({line, invite, sealed, telling, settled, seats, told}),
  // null when none were written. Snapshot semantics like golemNight; appended
  // LAST so the numeric wire mapping's existing keys are unchanged, and in
  // this template so it rides customRolesStripped to every client.
  golemNightCopy: null,
  // FT-1475 part 2: the role's whole stored night (steps, reminders,
  // spentToken, wakesWhenDead — the Studio re-opens from it) and whether the
  // reminder menu offers an "Ability spent" token. Appended LAST, in this
  // template so both ride customRolesStripped to every client.
  golemRoleNight: null,
  golemSpentToken: false,
  // FT-1491: the character's sections ([{ title, text, audience, with? }]),
  // null when it has none. Appended LAST, in this template so they ride
  // customRolesStripped — and customRolesForPlayers strips the
  // storyteller-only ones before a player's client receives them.
  golemSections: null,
};
const customRoleKeys = Object.keys(customRole);

/**
 * A custom character's fallback art: the official whose icon it borrows
 * (golemIcon, FT-851) when that id is bundled, else its team's generic token.
 */
function customImageAlt(role) {
  return (
    (role.golemIcon && rolesJSONbyId.has(role.golemIcon)
      ? role.golemIcon
      : null) ||
    {
      townsfolk: "good",
      outsider: "outsider",
      minion: "minion",
      demon: "evil",
      fabled: "fabled",
    }[role.team] ||
    "custom"
  );
}

export default new Vuex.Store({
  modules: {
    players,
    session,
    night,
  },
  state: {
    grimoire: {
      isNight: false,
      isNightOrder: true,
      // FT-1294 (2026-08-28, user grant): THE FACE-DOWN GRIMOIRE IS RETIRED,
      // flag and all. `isPublic` used to live here — the app's "the coins are
      // turned away from the viewer" switch, true meaning face DOWN. FT-1207
      // had already shut both doors to it (the R hotkey's branch and Menu's
      // Hide/Show), FT-999b had made revealed the resting state, and a
      // player's entry path committed false on the way in — so nothing any
      // user could reach ever set it. What was left was a dormant flag that
      // four code paths still WROTE and a dozen still READ, and it caused a
      // real bug (FT-1289: Play again turned every player's coins face down
      // and nothing turned them back). The coins are revealed, for everyone,
      // always; there is no state that can say otherwise.
      //
      // The readers it had were not all asking the same question — most were
      // standing in for "is this viewer the storyteller" (`!isSpectator`) or
      // "is a character sitting on this chair", and each was given its own
      // question directly rather than inheriting this one. See the notes at
      // golem/bluffs.js, golem/seatActions.js and Player.vue's belief chip.
      // Golem fork (2026-08-19): the demon's bluffs cluster, shown or hidden.
      // In the store rather than on TownSquare because the switch is a mark in
      // the MENU STRIP now (Menu.vue) and the cluster it drives is somewhere
      // else entirely. Defaults SHOWN on every viewport (user call) and is
      // stashed by persistence.js.
      isBluffsOpen: true,
      isStatic: false,
      isMuted: false,
      isImageOptIn: false,
      zoom: 0,
      background: "",
      // Golem fork (FT-936, moved to the store FT-991): the centre-face
      // splat's FROZEN seed for THIS client's view of the current game —
      // null until TownSquare.vue's created()/subscribe sets it (see that
      // file's own comment for what freezes it and why). In the store
      // rather than component-local data because TownInfo.vue reads it too
      // now (its `.info` background carries the splat, replacing the old
      // demon-head knocker) and TownInfo, unlike TownSquare, is NOT always
      // mounted — it unmounts and remounts around every nomination (App.vue
      // swaps it for Vote.vue) — so a component-local field would forget
      // the frozen mark on the very next vote. Never stashed to
      // localStorage (no persistence.js entry, deliberately) — per-tab,
      // per-load, same as it was as local data before this move.
      faceSplatSeed: null,
    },
    modals: {
      edition: false,
      fabled: false,
      gameState: false,
      reminder: false,
      role: false,
      roles: false,
      roleDrawer: false,
      // FT-857: the PLAYER's script drawer (right side) — the one surface for
      // the reference sheet and the night order.
      scriptDrawer: false,
      // FT-858: the VOTE HISTORY drawer (right side, beside the script) — the
      // nomination log's surface now.
      voteDrawer: false,
      // FT-860: a PLAYER's own night information — the third right-hand
      // drawer, and the only night surface a non-storyteller ever gets.
      nightDrawer: false,
      // FT-886: THE CHRONICLE — this game's own timeline, the fourth drawer on
      // the right-hand rail. It holds no state of its own: golem/chronicle
      // assembles it out of the night log, the vote history and the seats
      // every time it renders, so this flag is the whole of its footprint here.
      chronicleDrawer: false,
      // FT-965: THE TOWN CHAT — the fifth drawer on the right-hand rail, and
      // the only one that is not scoped to the game being played. Its contents
      // live in `chat` below rather than being reassembled per render, because
      // unlike the chronicle it is fed by the wire and cannot be derived.
      chatDrawer: false,
      // FT-1010: CHRONICLES — the town's whole story as one surface: the
      // chat log, the game events and the town records merged (user decision,
      // 2026-08-20). This is the drawer the strip's quill opens now. The two
      // flags above stay in place, unrouted — their components are retired by
      // unmounting, never deleted.
      chroniclesDrawer: false,
      // FT-1146: THE RECORDS PAGE — what has happened across every town, on
      // its own full-window surface (StatsOverlay.vue, the old town-records
      // overlay grown up). A modal rather than a component-local flag because
      // it is opened from three unrelated places — the entry screen, the
      // Chronicles drawer's boards line, and App's own strip wiring — and
      // `toggleModal` closing whatever else was open is exactly right: this
      // is a page you go to, not a panel you raise over the town.
      records: false,
      // FT-1341: THE CHANGE LOG, moved onto the right-hand rail — it was its
      // own centred face-plate (FT-1320); this flag is what lets it join the
      // drawer family's exclusive-open management instead of the local
      // component flag it used to carry (Menu.vue's old `changelogOpen`).
      changeLog: false,
    },
    /**
     * FT-965: THE TOWN'S ONE PERMANENT ROOM.
     *
     *  log        rows, ascending by `seq`, deduped by `seq`. Whispers this
     *             viewer was not party to never enter it (see `chatIngest`).
     *  syncedSeq  the high-water mark below which `log` is COMPLETE and
     *             CONTIGUOUS. Only the REST catch-up advances it; a live row
     *             arriving out of the blue proves nothing about its
     *             predecessors and must never move it. This separation is what
     *             makes a reconnect re-fetch the right range.
     *  scope      which slice is being LOOKED at — "town" | "game" | "none".
     *             A view, never a fetch: catch-up always reads the whole town.
     *  gameId     the game currently being played, or null between games. The
     *             host derives it; a player is told it on the gamestate sync.
     */
    chat: {
      log: [],
      syncedSeq: 0,
      syncing: false,
      scope: "town",
      gameId: null,
      error: "",
      /**
       * FT-1263: WHISPER TRAFFIC THIS BYSTANDER SAW FLY — local rows of
       * golem/whisperMarks' TRAFFIC_KIND, written by `chatMarkTraffic` when
       * a validated whisperMark lands and this viewer is NOT a party to it
       * (a party's own log holds the whisper row itself — that is their
       * record). Client ephemera, never merged into `log`: no store seq, no
       * wire row, forgotten on reload — the plane's memory, no more.
       */
      marks: [],
    },
    // FT-854: the role drawer's click-to-place selection (a role object,
    // or null) — clicking a seat's token places it
    drawerPick: null,
    // Which SEAT the armed character was picked up from, or null when it came
    // off a list (the tray, the grimoire drawer). This is the tap path's twin
    // of the drag's `golem/from` payload, and it is what makes tapping a
    // second seat a SWAP rather than an overwrite — the same trade the drag
    // has always done. Local view state: neither persisted nor synced.
    drawerPickFrom: null,
    // FT-854: when OFF (the default), a role lives in at most one chair —
    // placing it anywhere else MOVES it. Shared state so every placing path
    // (drag, click, assign, shuffle) obeys the same rule.
    allowDupRoles: false,
    /**
     * FT-1175: THE CHARACTERS THE STORYTELLER HAS SET ASIDE — role ids that
     * Deal must skip. Empty is the whole script, which is exactly how this
     * app has always dealt, so the feature costs nothing until it is used.
     *
     * IT IS A SET OF EXCLUSIONS, NOT A SET OF INCLUSIONS, and that is forced
     * rather than chosen: "which roles will be dealt" starts as ALL of them,
     * so a list of the included ones would have to be written out in full
     * before Deal worked at all, and a script swap would silently empty it.
     * A list of what is held back starts empty, means nothing until someone
     * means something by it, and a script swap simply leaves ids in it that
     * no longer match anything.
     *
     * AN ARRAY, NOT A Set: Vue 2's reactivity does not see into a Set, and
     * the tray paints one tile per role off this on every change.
     *
     * IT GOVERNS DEALING ONLY. An aimed placement — a drag onto a chair, the
     * tap path, the grimoire drawer — is the storyteller doing it on purpose
     * and is never refused; see RoleTray's own note.
     *
     * FT-1316 (user: roles all DE-selected on town open): the seed is FULL
     * now, not empty — a fresh script opens with every character set aside
     * and the storyteller lights the ones this game will use. The exclusion
     * SHAPE stays exactly as argued above (Deal-only, ids that stop matching
     * are inert); only where a fresh list starts moved. This state is
     * session-local (never persisted, never synced), so "fresh" is every
     * boot and every script swap — setEdition and setCustomRoles reseed the
     * same way, since a new script is a new selection to make.
     */
    dealExcluded: [...getRolesByEdition().keys()],
    // FT-857: which tab the script drawer opens on — "team" | "first" |
    // "other". The strip's night icon lands on "first".
    scriptDrawerView: "team",
    // FT-1019: which kind of line the chronicles stream shows — one of
    // golem/chronicles' FILTERS. Root state (not drawer-local) for the same
    // reason scriptDrawerView is: the V hotkey opens the drawer ALREADY
    // pointed at the gallows, so the pointer must exist before the drawer
    // does.
    chroniclesFilter: "all",
    // FT-1146: which recorded game the RECORDS page should open onto, or null
    // for its landing view. Root state for the same reason chroniclesFilter
    // is: the Chronicles drawer names the game BEFORE the page exists, and
    // `toggleModal` unmounts the drawer in the same breath — so the pointer
    // cannot live on either component. The page reads it once and clears it.
    recordsPick: null,
    edition: editionJSONbyId.get("tb"),
    roles: getRolesByEdition(),
    otherTravelers: getTravelersNotInEdition(),
    fabled,
  },
  actions: {
    /**
     * FT-965: CATCH THE TOWN LOG UP over REST, from the contiguity cursor to
     * the store's head, and leave the cursor pointing at the new frontier.
     *
     * Safe to call at any time and from anywhere — the socket calls it on
     * every (re)connect, the drawer calls it on open. Overlapping calls are
     * refused rather than queued: the second one would fetch the same range as
     * the first and merge to nothing.
     *
     * The pages land as they arrive (`chatIngest` per page) so a long history
     * fills in progressively, and every one of them is deduped against
     * whatever the socket delivered in the meantime.
     */
    async chatCatchUp({ state, commit }) {
      const town = state.session.sessionId;
      if (!town || state.chat.syncing) return;
      commit("chatSyncing", true);
      try {
        // FT-1349: say who is asking — the read is filtered server-side to
        // this requester. The viewer key is the same claim every sent line
        // carries; the edit key travels only when this browser is the
        // storyteller AND holds the town's key (an owned town), which is the
        // one identity the platform can verify and what unlocks the full log.
        const v = viewerOf(state);
        const auth = {
          viewer: v.key || "",
          editKey: (v.isStoryteller && editKeyFor(town)) || "",
        };
        const seq = await catchUp(
          town,
          state.chat.syncedSeq,
          (rows) => {
            commit("chatIngest", rows);
          },
          auth,
        );
        // The town can change under a fetch (a Back press, a hop between two
        // towns). `chatIngest` drops the rows themselves by townId; this stops
        // the OLD town's cursor being stamped onto the NEW town's empty log,
        // which would claim a completeness that was never fetched.
        if (state.session.sessionId === town) commit("chatSynced", seq);
      } catch (e) {
        commit("chatError", "Couldn't load the town log.");
      } finally {
        commit("chatSyncing", false);
      }
    },
  },
  getters: {
    /**
     * The current script in its compact wire form — what the edition frame
     * sends, what the game-state JSON copies out, what every client's `roles`
     * stash ends up holding. A bundled character travels as its id alone; a
     * custom one as { templateIndex: value } for each template property that
     * differs from the template's own default (identity, so an array the
     * role was given is kept even when empty). Night reminders and the
     * custom marker never travel; properties the template lacks are dropped.
     */
    customRolesStripped({ roles }) {
      const neverSent = [
        "firstNightReminder",
        "otherNightReminder",
        "isCustom",
      ];
      return Array.from(roles.values(), (role) => {
        if (!role.isCustom) return { id: role.id };
        const compact = {};
        for (const [key, value] of Object.entries(role)) {
          const slot = customRoleKeys.indexOf(key);
          if (slot === -1 || neverSent.includes(key)) continue;
          if (value === customRole[key]) continue;
          compact[slot] = value;
        }
        return compact;
      });
    },
    /**
     * FT-1491: customRolesStripped as a PLAYER may receive it — every custom
     * character's storyteller-only sections REMOVED, not merely hidden. The
     * edition frame (socket.js sendEdition) sends this; the storyteller's own
     * stash and copied-out game JSON keep the whole list.
     */
    customRolesForPlayers(state, getters) {
      const slot = customRoleKeys.indexOf("golemSections");
      return getters.customRolesStripped.map((entry) => {
        if (!Array.isArray(entry[slot])) return entry;
        const kept = playerSections(entry[slot]);
        const out = { ...entry };
        if (kept.length) out[slot] = kept;
        else delete out[slot];
        return out;
      });
    },
    rolesJSONbyId: () => rolesJSONbyId,
    /**
     * FT-1463: THE SCRIPT'S NIGHT ORDER — `{ firstNight: [id…], otherNight:
     * [id…] }`. The edition's own lists when it carries them (a custom script
     * saved since this card, or one from the official script tool), filtered
     * to what is still on the script; built from the roles' seed numbers
     * when it does not (an official edition, an older or foreign script).
     * See golem/nightOrder for the whole model.
     */
    nightLists({ edition, roles }) {
      const script = Array.from(roles.values());
      const out = {};
      NIGHTS.forEach((night) => {
        out[night] = resolveList(edition && edition[night], script, night);
      });
      return out;
    },
    /** FT-1463: `(role, night) → bool` — does it wake that night in THIS
     *  script? (golem/nightOrder's wakesIn.) */
    nightWakes({ roles }, getters) {
      const lists = getters.nightLists;
      const scriptIds = new Set();
      roles.forEach((role) => {
        if (!isTraveller(role)) scriptIds.add(role.id);
      });
      return (role, night) =>
        wakesIn(lists[night] || [], scriptIds, role, night);
    },
    /** FT-1463: `(role, night) → number` — the sort key every ordered night
     *  surface uses (golem/nightOrder's orderKey). */
    nightKey({ roles }, getters) {
      const lists = getters.nightLists;
      const lookup = (id) => roles.get(id) || rolesJSONbyId.get(id);
      return (role, night) => orderKey(lists[night] || [], role, night, lookup);
    },
  },
  mutations: {
    setZoom(state, zoom) {
      state.grimoire.zoom = zoom;
    },
    setBackground(state, background) {
      state.grimoire.background = background;
    },
    // FT-991: committed once per freeze by TownSquare.vue's own
    // created()/subscribe — see that file and grimoire.faceSplatSeed above.
    setFaceSplatSeed: set("faceSplatSeed"),
    toggleMuted(state, on) {
      toggle("isMuted")(state, on);
    },
    /** Golem fork (2026-08-19): show/hide the demon's bluffs cluster. */
    toggleBluffsOpen: toggle("isBluffsOpen"),
    toggleNightOrder(state, on) {
      toggle("isNightOrder")(state, on);
    },
    toggleStatic(state, on) {
      toggle("isStatic")(state, on);
    },
    /**
     * FT-860: the phase flip, and the ONE place the day counter moves.
     *
     * Every path that reaches night comes through here — the night sheet's
     * button, the S hotkey, the menu, and the socket's incoming gamestate —
     * so putting the increment here is what stops the counter drifting
     * between them. It fires only on a real day→night TRANSITION: a full
     * gamestate sync re-commits the current value on every reconnect, and
     * that must not count as a new night.
     */
    toggleNight(state, val) {
      const wasNight = state.grimoire.isNight;
      toggle("isNight")(state, val);
      if (!wasNight && state.grimoire.isNight) {
        state.night.day += 1;
        // FT-1454: the night ends the day's nominations — every client, on
        // the same genuine day→night turn (a resync's re-commit is not one).
        state.session.nominatedToday = { nominators: [], nominees: [] };
      }
    },
    /**
     * FT-1389: PLAY AGAIN's phase reset — isNight to FALSE, nothing else.
     * Deliberately NOT `toggleNight`: that commit's socket subscriber is
     * the phase BELL (the "Day X breaks." chronicle line, the automations,
     * the starpass retirement, the isNight broadcast), and a game being
     * reset is not a phase turning. Left un-reset, a game that ENDED at
     * night carried isNight into the next game — whose Start saw night
     * already standing, skipped its toggleNight, and lost Night 1's
     * increment and chronicle line (HostTools.beginAtNight's guard).
     * Not in the socket table on purpose: playAgain commits this before
     * `clearEnded`, whose full gamestate resync carries the fresh
     * isNight + nightDay to every client in the one sync they already get.
     */
    resetNight(state) {
      state.grimoire.isNight = false;
    },
    /**
     * FT-931: THE TOWN ENDS. The host declared a winner (EndGameOverlay);
     * this is the one commit every client applies — the host's own, and a
     * spectator's when it arrives over the wire (socket.js commits this
     * exact mutation on receipt, the same "one type, two callers" shape
     * `toggleNight` above already uses).
     *
     * It sets the result, and the reveal follows from the roles alone: once
     * the true role has reached this client (the gamestate sync, socket.js)
     * the existing render pipeline draws it — nothing here touches how a role
     * is drawn.
     *
     * FT-1294: this used to force `grimoire.isPublic = false` in the same
     * commit, because the coins could be face down. They cannot any more —
     * the flag is retired (see the state block above) — so the line went with
     * it. The reveal is unchanged: it was always the ARRIVAL OF THE ROLES
     * that made it, and the flag was already false on every client that could
     * reach this.
     */
    endGame(state, winningTeam) {
      state.session.isEnded = true;
      state.session.winningTeam = winningTeam === "evil" ? "evil" : "good";
      // FT-1003: the end reveal shows everyone everything — a per-seat
      // grimoire grant has nothing left to grant, on either side of the wire.
      state.session.grimoireGrants = {};
      state.session.isGrimoireGranted = false;
    },
    /**
     * FT-931: PLAY AGAIN. Only the result and the reveal are this
     * mutation's concern — the roster's roles clear through the existing
     * `players/clearRoles` action (same table, a new game), dispatched
     * alongside this from wherever Play again is pressed (App.vue).
     */
    clearEnded(state) {
      state.session.isEnded = false;
      state.session.winningTeam = null;
      // FT-1311 (the stuck noose): A NEW GAME STARTS WITH NO ONE ON THE
      // BLOCK. Nothing here or in players/clearRoles ever cleared
      // markedPlayer, so a town that ended with a mark standing carried it
      // into the next game — Play again re-dressed the same seat in a noose
      // it never earned. This mutation runs on EVERY client (the host's own
      // Play again, and a spectator's socket applies it on the resync), so
      // clearing it here clears it everywhere, the same reach-into-the-
      // submodule shape the grant fields below already use. Written direct
      // rather than through session/setMarkedPlayer so one commit stays the
      // whole reset; the breadcrumb that mutation logs is covered here:
      if (state.session.markedPlayer !== -1) {
        // eslint-disable-next-line no-console
        console.warn(
          `[noose] markedPlayer ${state.session.markedPlayer} → -1 (clearEnded — new game)`,
        );
        state.session.markedPlayer = -1;
      }
      // FT-1454: ...and with nobody having nominated or been nominated.
      state.session.nominatedToday = { nominators: [], nominees: [] };
      // FT-1294: THE LINE THAT USED TO SIT HERE IS GONE, and its whole class
      // of bug with it. This mutation runs on EVERY client, not just the
      // host's — a player's socket applies it on the Play again resync
      // (socket.js's `_updateGamestate` un-ends a client that still holds
      // isEnded) — so whatever face it wrote, it wrote on every screen in the
      // town. FT-1289 was exactly that: it wrote the coins face DOWN for
      // every player and nothing turned them back, and the next deal
      // delivered each character onto a coin that had stopped showing it. A
      // blank coin on their own named, claimed seat, and a whole town
      // reporting they never got a character. The role was always there; only
      // the face was wrong.
      //
      // FT-1289 fixed it by writing each viewer's own resting face. This
      // retires the question: there is no face to write. Play again leaves
      // the coins exactly as they were — revealed, for everyone.
      // FT-1003: a new game starts with no grimoire windows open anywhere —
      // the roles a grant delivered go through players/clearRoles alongside
      // this, exactly as the end reveal's do.
      state.session.grimoireGrants = {};
      state.session.isGrimoireGranted = false;
      // FT-1295: ...and the MEMORY dies here, in the one place the data it
      // describes dies. `players/clearRoles` (dispatched alongside this from
      // App.vue) wipes every non-traveller role, every reminder and all three
      // bluffs on a spectator's client — so a flag saying "I still hold what I
      // was shown" would be describing an empty table. `endGame` deliberately
      // does NOT clear it: ending the town wipes nothing, and a Spy who was
      // shown the grimoire still holds it while the result sits on screen.
      state.session.hasGrimoireMemory = false;
    },
    /*
     * FT-1294: `toggleGrimoire` STOOD DOWN AND REMOVED. It was FT-931's
     * mutation for the R hotkey and Menu's Hide/Show — the only two ways a
     * human could ever turn the coins over — and both of those doors were
     * shut in FT-1207. With the flag itself retired there is nothing left for
     * it to write, and leaving a mutation that could put the town back into a
     * state nothing else understands is precisely what this lane is for.
     */
    /**
     * FT-1003: THE GRANTED GRIMOIRE ARRIVES — one seat's client is shown the
     * whole town face-up. Never this client's own seat: the sender skips it,
     * so a seat whose belief differs from its truth can never learn the
     * difference from its own grant. The render path is FT-931's end reveal
     * verbatim — the true roles being PRESENT is the whole of it.
     *
     * FT-1294: this too used to write `isPublic = false` alongside. The coins
     * are always revealed now.
     *
     * FT-1295: THE GRIMOIRE IS NOT A ROLE LIST, and this is where it stops
     * being one. The payload is `{ seats, bluffs }`:
     *
     *   seats   [{ index, role, reminders }] — every seat the grimoire has
     *           something to say about, with the tokens laid beside it.
     *           FT-1381: `role` may be null — a role-less chair that carries
     *           tokens, or a character the client could not resolve; the
     *           tokens are owned either way, the coin is not written.
     *   bluffs  the demon's three, as resolved role objects
     *
     * WHAT A GRANT WRITES, IT OWNS. `reminders` is set for every seat in the
     * list, empty array included: a seat the storyteller has cleared must
     * lose the token the Spy remembers from last night, or the memory quietly
     * becomes a compost heap of everything ever true. Seats NOT in the list
     * are untouched — see revokeGrimoire for why that silence is the feature.
     *
     * THE BLUFFS ARE WRITTEN STRAIGHT INTO THE PLAYERS MODULE, in order from
     * slot 0: `setBluff` splices, and splicing index 2 of an empty array
     * appends at 0, so out-of-order writes arrive shuffled (socket.js's
     * `_updateBluffs` carries the same loop for the same reason). Reaching
     * into the submodule from a root mutation is the shape `endGame` and
     * `toggleNight` already use, and it is what lets ONE commit be the whole
     * arrival — the alternative is three mutations that can each land alone.
     */
    grantGrimoire(state, { seats, bluffs } = {}) {
      (seats || []).forEach(({ index, role, reminders }) => {
        const player = state.players.players[index];
        if (!player) return;
        // FT-1381: a null role is a seat the grant speaks about for its
        // TOKENS only — a role-less chair carrying tokens, or a character
        // this client could not resolve (socket.js's _updateGrimoireGrant).
        // The coin is left as the holder has it (their own guess, or empty);
        // writing `null` over it would crash every `player.role.id` read.
        if (role) player.role = role;
        player.reminders = reminders || [];
      });
      if (Array.isArray(bluffs)) {
        bluffs.forEach((role, index) => {
          state.players.bluffs.splice(index, 1, role || {});
        });
      }
      state.session.isGrimoireGranted = true;
      state.session.hasGrimoireMemory = true;
    },
    /**
     * FT-1003 / FT-1295: THE WINDOW CLOSES AND NOTHING IS WIPED.
     *
     * This used to walk every seat that was not the viewer's own and not a
     * traveller and set `player.role = {}`. That slot is the SAME one the
     * player's own guesses live in — a player's role edits never leave their
     * browser (socket.js's `sendPlayer` returns early for a spectator), so on
     * their client `player.role` IS their guess about that chair. The grant
     * wrote truth over the guesses when it opened, and this wiped both when it
     * closed: the user's report, verbatim — "ending the night resets the spy's
     * grimoire so they are losing the 'guesses' they made for the users roles".
     *
     * A MEMORY, NOT A CAMERA — the user's call, made against a read-only
     * overlay ("it needs to be editable by the spy and when night ends it
     * would just be the same thing basically"). So the close stops UPDATING:
     * the host stops pushing, and what the Spy saw stays on their own board,
     * theirs to edit, until the storyteller shows them the grimoire again and
     * a fresh look overwrites what it speaks about.
     *
     * TWO CONSEQUENCES, BOTH DELIBERATE:
     *   · a fresh look OVERWRITES their edits on any seat it speaks about —
     *     correct, they just looked at that seat;
     *   · a seat the grant is SILENT about keeps the old memory. Their own
     *     chair (the sender skips it), and any seat holding no character at
     *     that moment. That staleness is the difference between the memory
     *     the user chose and the camera they rejected.
     *
     * The old wipe's traveller exemption went with the wipe: it existed
     * because traveller roles are public knowledge and clearing them would
     * have taken away something the client was entitled to anyway. Nothing is
     * cleared now, so there is nothing to exempt.
     *
     * Still idempotent, and still commits nothing when no window was open — a
     * revoke frame arriving at a client that was never granted (a joiner's
     * self-healing sync) leaves `hasGrimoireMemory` exactly as it found it.
     */
    revokeGrimoire(state) {
      state.session.isGrimoireGranted = false;
    },
    toggleImageOptIn(state, on) {
      toggle("isImageOptIn")(state, on);
    },
    setAllowDupRoles(state, on) {
      state.allowDupRoles = !!on;
    },
    /** FT-1175: set a character aside from the deal, or put it back. One
     *  mutation for both directions — the tray's tiles are a toggle, and a
     *  pair of add/remove mutations would only give a caller a way to get
     *  the two out of step. */
    toggleDealExcluded(state, id) {
      if (!id) return;
      const at = state.dealExcluded.indexOf(id);
      if (at === -1) state.dealExcluded.push(id);
      else state.dealExcluded.splice(at, 1);
    },
    /** Every character back in the deal. */
    clearDealExcluded(state) {
      state.dealExcluded = [];
    },
    /** Arm a character. The source seat always resets here, so a pick made
     *  from a list can never inherit the last seat-pick's origin — a caller
     *  that means "from this chair" says so with setDrawerPickFrom after. */
    setDrawerPick(state, role) {
      state.drawerPick = role || null;
      state.drawerPickFrom = null;
    },
    setDrawerPickFrom(state, seat) {
      state.drawerPickFrom = typeof seat === "number" ? seat : null;
    },
    /** FT-857: point the script drawer at one of its three tabs. */
    setScriptDrawerView(state, view) {
      state.scriptDrawerView = view || "team";
    },
    /** FT-1019: point the chronicles stream at one of its filters (the V
     *  hotkey lands on "gallows"; the drawer's own cells commit this too). */
    setChroniclesFilter(state, filter) {
      state.chroniclesFilter = FILTERS.includes(filter) ? filter : "all";
    },
    /** FT-1146: name the recorded game the Records page should open onto
     *  (null = its landing view). Set by whoever opens the page; the page
     *  clears it as it reads, so a second visit lands on the landing again. */
    setRecordsPick(state, id) {
      state.recordsPick = id || null;
    },
    /**
     * FT-965: THE ONE DOOR INTO THE LOG. Both feeds come through here — the
     * socket's live rows and the catch-up's pages — so the two rules that make
     * the log correct are stated once and cannot be applied to one path and
     * forgotten on the other:
     *
     *   1. A row for a DIFFERENT TOWN is dropped. The stored row carries its
     *      own `townId`, so a page still in flight when the browser hops towns
     *      cannot land in the new town's log.
     *   2. A whisper this viewer was not party to is dropped BEFORE the store.
     *      The catch-up GET is unauthenticated and returns every row in the
     *      town — the relay's live-path guard cannot help there, so this is
     *      where a whisper stops. Never rendered, because never held.
     *
     * Dedup and ordering are `mergeLog`'s, keyed on the store's per-town `seq`.
     */
    chatIngest(state, rows) {
      if (!Array.isArray(rows) || !rows.length) return;
      const town = state.session.sessionId;
      const viewer = viewerOf(state);
      const allowed = rows.filter(
        (row) =>
          row &&
          typeof row.seq === "number" &&
          row.townId === town &&
          // FT-1010: the live game's id rides along — a whisper from a
          // FINISHED game is public (see canSee), and "finished" can only be
          // judged against the game being played right now.
          canSee(row, viewer, state.chat.gameId) &&
          // FT-1206: THE CHAT LEVEL'S SECOND GATE. The composer refuses to
          // build a forbidden line; this drops one a bypassing client sent
          // anyway, before the store, live game only — finished games are
          // published history and the storyteller is never silenced (the
          // helper owns both rules). socket.js re-reads the log when the
          // level changes, so a relaxed level re-offers what this dropped.
          !levelSilences(
            row,
            viewer,
            state.chat.gameId,
            towerState.chatLevel,
            state.players.players.length,
          ),
      );
      state.chat.log = mergeLog(state.chat.log, allowed);
    },
    /**
     * FT-1263: A PLANE FLEW AND THIS VIEWER WAS NOT ON IT — keep its memory
     * as a local traffic row. Called by socket.js beside the plane dispatch,
     * so the gate chain is the plane's own: marks on at SEND (Off is quiet
     * on the wire), shape validated (cleanMark). Here only the party test
     * remains: the storyteller and the whisper's two seats hold the whisper
     * row itself — their record — and write nothing.
     *
     * The row wears the whisper row's own field names (senderKey /
     * recipientKey carry SEAT NAMES, read from the ring at the moment the
     * plane flew) so the Chronicle renders it with the machinery it already
     * has. `seq` is synthetic — after the newest row this client holds,
     * clear of the +0.5/+0.6 the opening board and the night blocks use,
     * monotonic across marks — the FT-1057 splice idiom exactly.
     */
    chatMarkTraffic(state, mark) {
      const viewer = viewerOf(state);
      if (viewer.isStoryteller) return;
      const seat = seatOf(state);
      if (seat === mark.from || seat === mark.to) return;
      const players = state.players.players;
      const nameAt = (i) => (players[i] && players[i].name) || `Seat ${i + 1}`;
      const log = state.chat.log;
      const base = log.length ? log[log.length - 1].seq : 0;
      const marks = state.chat.marks;
      const prev = marks.length ? marks[marks.length - 1].seq : 0;
      const seq = Math.max(base + 0.7, prev + 0.001);
      marks.push({
        id: "traffic:" + seq,
        seq,
        kind: TRAFFIC_KIND,
        gameId: state.chat.gameId,
        senderKey: nameAt(mark.from),
        recipientKey: nameAt(mark.to),
        senderSeat: mark.from,
        recipientSeat: mark.to,
        createdAt: new Date().toISOString(),
        // the moment, stamped the way sendChat stamps a row's: the running
        // phase and day, and no day at all once the game has ended
        phase: state.grimoire.isNight ? "night" : "day",
        dayNumber: state.session.isEnded ? null : state.night.day,
      });
    },
    chatSyncing(state, on) {
      state.chat.syncing = !!on;
    },
    /** The contiguity cursor. Monotonic — only the REST loop ever calls it. */
    chatSynced(state, seq) {
      if (Number.isFinite(seq) && seq > state.chat.syncedSeq) {
        state.chat.syncedSeq = seq;
      }
    },
    /**
     * Forget the log entirely — a different town, or a different VIEWER.
     *
     * The viewer matters as much as the town: rows are filtered on the way in,
     * so a browser that takes a seat (or hands the storyteller's chair over)
     * now has a different answer to "may I see this", and the rows it dropped
     * under its old identity will never be re-offered by a cursor that has
     * already moved past them. Resetting sends the catch-up back to zero,
     * where it re-reads the whole log against the identity now in force.
     */
    chatReset(state) {
      state.chat.log = [];
      state.chat.syncedSeq = 0;
      state.chat.syncing = false;
      state.chat.error = "";
      // FT-1263: another town's (or another identity's) planes are not this
      // viewer's memory — the party test was made against the old identity.
      state.chat.marks = [];
    },
    chatSetGameId(state, id) {
      state.chat.gameId = id || null;
    },
    chatSetScope(state, scope) {
      state.chat.scope = SCOPES.includes(scope) ? scope : "town";
    },
    chatError(state, message) {
      state.chat.error = message || "";
    },
    /**
     * SAY SOMETHING. The mutation is what travels — socket.js's subscriber
     * turns it into the wire frame, exactly as `session/callBack` and every
     * other broadcast in this app does. Nothing is appended to the log here:
     * a line reaches the log when the STORE has accepted it and the relay
     * echoes the row back, so what is on screen is only ever what was recorded.
     */
    chatSay(state) {
      state.chat.error = "";
    },
    /** Open (or shut) one modal; every other modal closes. Bare → all shut. */
    toggleModal({ modals }, name) {
      for (const key of Object.keys(modals)) {
        modals[key] = key === name ? !modals[key] : false;
      }
    },
    /**
     * FT-1459: the HOUSE OVERLAY just rewrote official characters in place
     * (golem/houseRoles). The bundled objects already carry the new text;
     * this re-issues the maps so every surface re-renders, and re-derives the
     * CLONES a script keeps for its own night order — base text, the clone's
     * own night numbers (night order is the script's, never the house's).
     */
    houseRolesApplied(state) {
      const fresh = (role) => {
        const b = rolesJSONbyId.get(role.id);
        if (!b || b === role) return role;
        return Object.assign({}, b, {
          firstNight: role.firstNight,
          otherNight: role.otherNight,
        });
      };
      // FT-1485: the rows decide the team too — re-sorted so a character
      // whose team moved lands in its new group (the sort is stable, so an
      // unmoved script keeps its order).
      state.roles = new Map(
        Array.from(state.roles, ([id, role]) => [id, fresh(role)]).sort(
          ([, a], [, b]) => byTeamReversed(a, b),
        ),
      );
      state.otherTravelers = new Map(state.otherTravelers);
    },
    /**
     * FT-1484 step 3: EDITS REACH EVERY SCRIPT — the latest library versions
     * of this script's custom characters (golem/houseRoles' live overlay),
     * laid onto the loaded ones IN PLACE, matched by library id
     * (`golemRoleId`), so every holder of the object — a seat, a bluff, the
     * night sheet — reads the new text with no second lookup. `fresh` =
     * Map(libraryId → app role, roles.js toAppRole). Only LIVE_ROLE_KEYS
     * move; the script keeps its app id, a baked icon, the art's fit and its
     * deal selection (this is the same script, not a new one — setCustomRoles
     * would reseed the deal). A role with no library id (an imported script's
     * own) has nothing to follow and stays as it is.
     */
    liveRolesApplied(state, fresh) {
      const customFabled = [...state.fabled.values()].filter((r) => r.isCustom);
      const script = [...state.roles.values(), ...customFabled];
      let touched = 0;
      script.forEach((role) => {
        const next =
          role.isCustom &&
          role.golemRoleId &&
          fresh &&
          fresh.get(role.golemRoleId);
        if (!next) return;
        LIVE_ROLE_KEYS.forEach((key) => {
          const value =
            key in next
              ? next[key]
              : key in customRole
              ? customRole[key]
              : undefined;
          if (value === undefined) Vue.delete(role, key);
          else Vue.set(role, key, value);
        });
        if (role.golemIconData) role.image = role.golemIconData;
        else Vue.set(role, "image", next.image || "");
        Vue.set(role, "imageAlt", customImageAlt(role));
        touched += 1;
      });
      if (!touched) return;
      // the night registries rebuild whole with the script, as setCustomRoles
      // rebuilds them
      resetAuthoredNight();
      script.forEach((role) => {
        if (role.golemNight) registerAuthoredNight(role.id, role.golemNight);
      });
      registerScriptRoleCopy(
        script
          .filter((role) => !rolesJSONbyId.has(role.id))
          .map((role) => [role.id, role.golemNightCopy]),
      );
      // re-issue the maps (every surface re-renders); a team change can move
      // a character between the script and the Fabled
      const sorted = script.slice().sort(byTeamReversed);
      state.roles = new Map(
        sorted.filter((r) => r.team !== "fabled").map((r) => [r.id, r]),
      );
      state.fabled = new Map([
        ...sorted.filter((r) => r.team === "fabled").map((r) => [r.id, r]),
        ...fabledJSON.map((role) => [role.id, role]),
      ]);
    },
    /**
     * Load a custom script's characters. Entries arrive named ({ id, name,
     * … }, or a bare { id } for a bundled character) or in the compact
     * numeric form customRolesStripped produces.
     */
    /**
     * FT-1484: forget the LOADED copy of custom role `id` about to be replaced
     * in place (same id — an owner's or a keyed library save). setCustomRoles
     * lets an already-loaded character win over its entry, so without this a
     * same-id save keeps the stale copy. The next setCustomRoles rebuilds the
     * map, so this only ever makes that one lookup miss.
     */
    forgetLoadedRole(state, id) {
      state.roles.delete(id);
    },
    setCustomRoles(state, roles) {
      const processedRoles = roles
        // numeric form → named: "3" is the template's fourth key, and so on.
        // An entry counts as numeric when its id slot ("0") holds something.
        .map((entry) => {
          if (!entry["0"]) return entry;
          const named = {};
          customRoleKeys.forEach((key, slot) => {
            if (Object.prototype.hasOwnProperty.call(entry, slot)) {
              named[key] = entry[slot];
            }
          });
          return named;
        })
        .map((role) => {
          // normalized ON the entry itself — the `roles` stash holds the
          // caller's objects, so it keeps the normalized ids too
          role.id = clean(role.id);
          return role;
        })
        .map((role) => {
          const out =
            // a bundled or already-loaded character wins over the entry
            rolesJSONbyId.get(role.id) ||
            state.roles.get(role.id) ||
            Object.assign({}, customRole, role);
          // Golem fork (FT-854): per-script NIGHT-ORDER overrides — the
          // Almanac's drag-reorder used to write firstNight/otherNight onto
          // the script entry; overlay them on a CLONE (bases are shared
          // objects). FT-1463: nothing WRITES these any more (the script owns
          // ordered lists); this overlay stays as the MIGRATION — a script
          // saved before the lists builds them from these numbers, and its
          // next edit materializes the lists and drops the numbers.
          if (
            out !== role &&
            (role.firstNight !== undefined || role.otherNight !== undefined)
          ) {
            const f =
              role.firstNight !== undefined ? role.firstNight : out.firstNight;
            const o =
              role.otherNight !== undefined ? role.otherNight : out.otherNight;
            if (f !== out.firstNight || o !== out.otherNight) {
              return Object.assign({}, out, { firstNight: f, otherNight: o });
            }
          }
          return out;
        })
        // custom characters only: icon fallbacks + night slots kept >= 0
        .map((role) => {
          if (rolesJSONbyId.has(role.id)) return role;
          // Golem fork (FT-856): a locally BAKED icon (library pick or
          // upload run through the engraver) rides the role as a data URL —
          // no network, so it renders without the remote-image opt-in.
          if (role.golemIconData) role.image = role.golemIconData;
          // Golem fork (FT-851): a custom role that borrows an OFFICIAL
          // role's icon names it in golemIcon — the bundled asset renders
          // without the remote-image opt-in. Unknown ids fall back to the
          // team-generic token.
          role.imageAlt = customImageAlt(role);
          for (const night of ["firstNight", "otherNight"]) {
            role[night] = Math.abs(role[night]);
          }
          return role;
        })
        .filter((role) => role.name && role.ability && role.team)
        .sort(byTeamReversed);
      // FT-1040: register every forged role's composed night action — the
      // whole registry rebuilds with the script, on every client this
      // mutation runs on (local load, vault load, the socket's edition
      // frame). A role without one, or with one that doesn't sanitize,
      // simply stays unlisted — the free-text fallback, as before.
      resetAuthoredNight();
      processedRoles.forEach((role) => {
        if (role.golemNight) registerAuthoredNight(role.id, role.golemNight);
      });
      // FT-1475: …and every custom role's own night WORDS, the same way —
      // rebuilt whole with the script (nightSigns' role copy layer, rung 2).
      registerScriptRoleCopy(
        processedRoles
          .filter((role) => !rolesJSONbyId.has(role.id))
          .map((role) => [role.id, role.golemNightCopy]),
      );
      state.roles = new Map(
        processedRoles
          .filter((role) => role.team !== "fabled")
          .map((role) => [role.id, role]),
      );
      // FT-1316: a new script is a new selection — reseed the deal exclusion
      // FULL (all set aside) so the storyteller lights this game's cast by
      // hand. See dealExcluded's own note in state.
      state.dealExcluded = [...state.roles.keys()];
      state.fabled = new Map([
        ...processedRoles
          .filter((r) => r.team === "fabled")
          .map((r) => [r.id, r]),
        ...fabledJSON.map((role) => [role.id, role]),
      ]);
      state.otherTravelers = new Map(
        rolesJSON
          .filter(
            (r) => r.team === "traveler" && !roles.some((i) => i.id === r.id),
          )
          .map((role) => [role.id, role]),
      );
    },
    /**
     * Load an edition. A bundled one is taken from the bundle (the payload
     * may be just its id) and brings its characters with it; anything else
     * is a custom script's edition, whose characters come by setCustomRoles.
     * `edition` is reassigned below — keep it a plain parameter.
     */
    setEdition(state, edition) {
      const bundled = editionJSONbyId.get(edition.id);
      if (bundled) {
        state.edition = bundled;
        state.roles = getRolesByEdition(bundled);
        state.otherTravelers = getTravelersNotInEdition(bundled);
        // FT-1316: a new script is a new selection — reseed the deal
        // exclusion FULL (all set aside; see dealExcluded's note in state).
        // Inside the known-edition branch on purpose: a custom edition's
        // roles arrive through setCustomRoles, which reseeds off the
        // processed list itself.
        state.dealExcluded = [...state.roles.keys()];
        // FT-1475: an official edition has no custom roles, so no custom
        // roles' own words
        registerScriptRoleCopy([]);
      } else {
        // FT-1486: a script's `_meta.nightCopy` (lifted onto the edition by
        // parseRoles) rides along untouched, so its saved JSON keeps it —
        // but nothing reads it: a character's wording lives on the character.
        // FT-1463: the script's two night-order lists, washed on the same
        // door (cleaned ids, no duplicates). Anything that is not a list is
        // dropped, which is the "build it from the seeds" signal.
        NIGHTS.forEach((night) => {
          if (!(night in edition)) return;
          const list = sanitizeList(edition[night]);
          edition = Object.assign({}, edition);
          if (list) edition[night] = list;
          else delete edition[night];
        });
        state.edition = edition;
      }
      state.modals.edition = false;
    },
  },
  plugins: [persistence, deathMoment, socket],
});
